/* ============================================================
   "This browser is not keeping your work."

   idb.putQuiet and session.set already refuse to throw, and that is
   right — audit A-06: losing persistence must never take the on-screen
   result with it. But "does not throw" was doing double duty as "does
   not need to be mentioned", and those are different claims.

   putQuiet's own comment argues that an automatic save is a convenience
   and the render is the product, so losing one costs the user nothing
   they asked for. That holds right up to the moment they open /saved/
   and their corridor run is not there. Nothing lied to them; nothing
   told them either. The console said "Could not persist to
   'benchmarks'" — to a console nobody in this product's audience has
   open, on connections where a second attempt is expensive.

   So the failure keeps its no-throw contract and gains a voice. One
   notice, once per session, naming the cause, what still works, what is
   lost, and what to do instead.
   ============================================================ */

const EVENT = 'kanda:storage-refused';

/** Fired by the storage layer. Kept as an event so storage.js stays a
    storage module and does not grow a dependency on the DOM. */
export function announceStorageRefused(detail) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EVENT, { detail }));
}

/* A full quota and a blocked store have different fixes, and "a storage
   error occurred" helps with neither. */
function cause(error) {
  const name = error && error.name;
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') {
    return 'This browser has no room left for this site.';
  }
  if (/unavailable|blocked/i.test(String((error && error.message) || ''))) {
    return 'This browser is not allowing this site to store anything — private windows usually are not.';
  }
  return 'This browser refused to store this result.';
}

let shown = false;

function render(error) {
  if (shown || typeof document === 'undefined' || !document.body) return;
  shown = true;

  const bar = document.createElement('div');
  bar.className = 'notice notice-warning storage-refused no-print';
  bar.setAttribute('role', 'alert');

  const text = document.createElement('p');
  text.textContent =
    `${cause(error)} Everything on this page is still correct and will still print, ` +
    'but it will not appear under Saved and it will be gone when you close this tab. ' +
    'Export the PDF before you leave.';

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'storage-refused-close';
  dismiss.setAttribute('aria-label', 'Dismiss');
  dismiss.textContent = '×';
  dismiss.addEventListener('click', () => bar.remove());

  bar.append(text, dismiss);
  /* Appended, not prepended: role="alert" is announced on insertion
     wherever it sits, so putting it first would only cost a keyboard
     user the skip link by making this the page's first tab stop. */
  document.body.append(bar);
}

/** Called once from the app entry point. */
export function mountStorageNotice() {
  if (typeof window === 'undefined') return;
  window.addEventListener(EVENT, (e) => render(e.detail && e.detail.error));
}
